// mock-api.ts
import moment from 'moment';
import {
  PostAssetUploadParameters,
  PostAuthRegisterParameters,
  PostAuthLoginParameters,
  PostAuthSendVerificationCodeParameters,
  PostAuthCheckAccountExistsParameters,
  PostCampaignParameters,
  GetCampaignListParameters,
  GetCampaignParameters,
  PutCampaignParameters,
  DeleteCampaignParameters,
  GetCampaignThemeParameters,
  PostDonationParameters,
  GetDonationListParameters,
  GetDonationParameters,
  PutDonationParameters,
  GetUserParameters,
  PutUserParameters,
  CampaignStatus,
  ReceiptOption,
  CommunicationLanguage,
  CampaignAsset,
  PostAuthConfirmEmailParameters,
  PostPaymePaymentParameters,
  PostContactParameters
} from './api-parameters-type';

import {
  PostAssetUploadResult,
  GetAssetResult,
  PostAuthRegisterResult,
  PostAuthLoginResult,
  PostAuthSendVerificationCodeResult,
  PostAuthCheckAccountExistsResult,
  PostAuthLogoutResult,
  PostCampaignResult,
  GetCampaignListResult,
  GetCampaignResult,
  PutCampaignResult,
  DeleteCampaignResult,
  GetCampaignThemeResult,
  PostDonationResult,
  GetDonationListResult,
  GetDonationResult,
  PutDonationResult,
  GetUserResult,
  PutUserResult,
  PostAuthConfirmEmailResult,
  GetCampaignThemeListResult,
  PostPaymePaymentResult,
  PostContactResult,
} from './api-result';
import config from '../config';

const MOCK_TOTAL = Math.floor(Math.random() * 56) + 65; // 65 ~ 120
let index = 0
function randomId() {
  const str = '' + Date.now() + index++
  return str.substring(str.length - 10, str.length)
}

// 生成指定长度的中英文或英文文本
function genText(len: number, zh = true) {
  // 计算一个随机长度，范围在 [len*0.7, len*1.3]
  const min = Math.floor(len * 0.4);
  const max = Math.ceil(len * 1.3);
  const realLen = Math.floor(Math.random() * (max - min + 1)) + min;

  const zhChars = "的一是在不了有和人这中大为上个国我以要他时来用们生到作地于出就分对成会可主发年动同工也能下过子说产种面而方后多定行学法所民得经十三之进着等部度家电力里如水化高自二理起小物现实加量都两体制机当使点从业本去把性好应开它合还因由其些然前外天政四日那社义事平形相全表间样与关各重新线内数正心反你明看原又么利比或但质气第向道命此变条只没结解问意建月公无系军很情者最立代想已通并提直题党程展五果料象员革位入常文总次品式活设及管特件长求老头基资边流路级少图山统接知较将组见计别她手角期根论运农指几九区强放决西被干做必战先回则任取据处队南给色光门即保治北造百规热领七海口东导器压志世金增争济阶油思术极交受联什认六共权收证改清己美再采转更单风切打白教速花带安场身车例真务具万每目至达走积示议声报斗完类八离华名确才科张信马节话米整空元况今集温传土许步群广石记需段研界拉林律叫且究观越织装影算低持音众书布复容儿须际商非验连断深难近矿千周委素技备半办青省列习响约支般史感劳便团往酸历市克何除消构府称太准精值号率族维划选标写存候毛亲快效斯院查江型眼王按格养易置派层片始却专状育厂京识适属圆包火住调满县局照参红细引听该铁价严龙飞";
  let text = "";
  if (zh) {
    while (text.length < realLen) {
      text += zhChars[Math.floor(Math.random() * zhChars.length)];
    }
  } else {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ ";
    while (text.length < realLen) {
      text += chars[Math.floor(Math.random() * chars.length)];
    }
  }
  return text;
}


// 真实图片地址生成器
function genImageUrl(width = 600, height = 400, seed = '') {
  return `https://picsum.photos/seed/${seed || randomId()}/${width}/${height}`;
}

// 随机 pick
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// 错误码
const errorCodes = ['INVALID_PARAMS', 'INVALID_PERMISSION', 'INVALID_TOKEN'] as const;

// 模拟网络延迟
function delay<T>(data: T, ms = 600): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(data), ms));
}

function mockError(kind?: string): any {
  const errKind = kind || pick([...errorCodes]);
  return {
    kind: errKind,
    status: 400,
    message: `模拟错误${errKind}`,
  };
}

// ------ 资源（资产） ------
export async function PostAssetUpload(
  params: PostAssetUploadParameters,
  forceError?: boolean
): Promise<PostAssetUploadResult> {
  if (forceError) return delay(mockError());
  return delay({
    kind: 'ok',
    data: {
      id: randomId(),
      mimeType: 'image/png',
      size: 1024 + Math.floor(Math.random() * 10000),
      thumbnailHeight: 120,
      thumbnailUrl: genImageUrl(200, 120),
      thumbnailWidth: 200,
      url: genImageUrl(600, 400)
    }
  });
}

export async function GetAsset(
  id: string,
  forceError?: boolean
): Promise<GetAssetResult> {
  if (forceError) return delay(mockError());
  return delay({
    kind: 'ok',
    data: {
      id,
      mimeType: 'image/jpeg',
      size: 2333,
      thumbnailHeight: 120,
      thumbnailUrl: genImageUrl(200, 120, id),
      thumbnailWidth: 200,
      url: genImageUrl(800, 400, id)
    }
  });
}

// ------ 用户注册/登录/验证码/登出 ------
export async function PostAuthRegister(
  params: PostAuthRegisterParameters,
  forceError?: boolean
): Promise<PostAuthRegisterResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: randomId() } });
}

export async function PostAuthLogin(
  params: PostAuthLoginParameters,
  forceError?: boolean
): Promise<PostAuthLoginResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: randomId() } });
}

export async function PostAuthSendVerificationCode(
  params: PostAuthSendVerificationCodeParameters,
  forceError?: boolean
): Promise<PostAuthSendVerificationCodeResult> {
  if (forceError) return delay(mockError());
  return delay({
    kind: 'ok',
    data: {
      countdown: 50,
      expireTime: moment().add(60, 's').toISOString()
    }
  });
}

export async function PostAuthCheckAccountExists(
  params: PostAuthCheckAccountExistsParameters,
  forceError?: boolean
): Promise<PostAuthCheckAccountExistsResult> {
  if (forceError) return delay(mockError());
  return delay({
    kind: 'ok',
    data: {
      exists: false,
      type: params.username.includes('@') ? 'email' : 'phone',
    }
  });
}

export async function PostAuthConfirmEmail(
  params: PostAuthConfirmEmailParameters,
  forceError?: boolean
): Promise<PostAuthConfirmEmailResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { message: '邮箱已验证', success: true } });
}

export async function PostAuthLogout(
  forceError?: boolean
): Promise<PostAuthLogoutResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: {} });
}

// ------ 筹款活动相关 ------
const campaignTotal = MOCK_TOTAL;
const campaignList: any[] = [];
for (let i = 0; i < campaignTotal; i++) {
  const id = i + ''
  campaignList.push({
    id,
    title: genText(20),
    subTitle: genText(80, true),
    campaignThemeId: randomId(),
    description: genText(40, true),
    duration: 30 + Math.floor(Math.random() * 20),
    eventName: genText(8),
    expectedEndDate: moment().add(30 + i, 'days').format('YYYY-MM-DD'),
    expectedLaunchDate: moment().subtract(i, 'days').format('YYYY-MM-DD'),
    goalAmount: Math.floor(Math.random() * 10000),
    hostName: genText(4, true),
    campaignAssets: [{
      id: randomId(),
      url: genImageUrl(600, 400, id),
      thumbnailUrl: genImageUrl(200, 120, id),
      mimeType: "image/jpeg",
      position: 'cover'
    }, {
      id: randomId(),
      url: genImageUrl(600, 400, id),
      thumbnailUrl: genImageUrl(200, 120, id),
      mimeType: "image/jpeg",
      position: 'profile'
    },],
    raisedAmount: Math.floor(Math.random() * 5000),
    status: pick(['pending', 'ongoing', 'paused', 'completed', 'cancelled', 'expired']),
    userId: randomId(),
  });
}
function getPagedList<T>(list: T[], limit: number = 10, lastId?: string) {
  let startIdx = 0;
  if (lastId) {
    startIdx = list.findIndex((x: any) => x.id === lastId) + 1;
    if (startIdx < 0) startIdx = 0;
  }
  const items = list.slice(startIdx, startIdx + limit);
  return { items, total: list.length };
}

export async function PostCampaign(
  params: PostCampaignParameters,
  forceError?: boolean
): Promise<PostCampaignResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: randomId() } });
}

export async function GetCampaignList(
  params: GetCampaignListParameters,
  forceError?: boolean
): Promise<GetCampaignListResult> {
  if (forceError) return delay(mockError());
  const { limit = 10, lastId } = params;
  const { items, total } = getPagedList(campaignList, limit, lastId);
  return delay({
    kind: 'ok',
    data: {
      items,
      total
    }
  });
}

export async function GetCampaign(
  params: GetCampaignParameters,
  forceError?: boolean
): Promise<GetCampaignResult> {
  if (forceError) return delay(mockError());
  const item = campaignList.find(x => x.id === params.id) || campaignList[0];
  return delay({
    kind: 'ok',
    data: item
  });
}

export async function PutCampaign(
  params: PutCampaignParameters,
  forceError?: boolean
): Promise<PutCampaignResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: params.id } });
}

export async function DeleteCampaign(
  params: DeleteCampaignParameters,
  forceError?: boolean
): Promise<DeleteCampaignResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: {} });
}

// ------ 活动主题相关 ------
const themeTotal = 20;
const campaignThemeList: any[] = [];
for (let i = 0; i < themeTotal; i++) {
  const id = randomId();
  campaignThemeList.push({
    id,
    name: genText(8, true),
    asset: {
      id: randomId(),
      url: genImageUrl(600, 400, id),
      thumbnailUrl: genImageUrl(200, 140, id),
      mimeType: "image/png"
    },
    assetId: randomId(),
    order: i + 1,
    updatedAt: new Date()
  });
}
export async function GetCampaignThemeList(
  params: any,
  forceError?: boolean
): Promise<GetCampaignThemeListResult> {
  if (forceError) return delay(mockError());
  const { limit = 10, lastId } = params;
  const { items, total } = getPagedList(campaignThemeList, limit, lastId);
  return delay({
    kind: 'ok',
    data: {
      items,
      total
    }
  });
}
export async function GetCampaignTheme(
  params: GetCampaignThemeParameters,
  forceError?: boolean
): Promise<GetCampaignThemeResult> {
  if (forceError) return delay(mockError());
  const item = campaignThemeList.find(x => x.id === params.id) || campaignThemeList[0];
  return delay({
    kind: 'ok',
    data: item
  });
}

// ------ 捐款相关 ------
const donationTotal = Math.floor(Math.random() * 56) + 65;
const donationList: any[] = [];
for (let i = 0; i < donationTotal; i++) {
  const id = i;
  const campaign = campaignList.find(x => x.id == id) || campaignList[0];
  donationList.push({
    id,
    amount: 100 * (i + 1),
    campaign,
    campaignId: randomId(),
    donorEmail: `donor${i + 1}@test.com`,
    donorName: genText(15, true),
    isAnonymous: Math.random() > 0.5,
    message: genText(18, true),
    paymentMethod: pick(['alipay', 'wechat', 'credit_card']),
    receiptOption: pick(['e_receipt', 'no_thanks', 'receipt_by_post']),
    user_id: randomId(),
  });
}
export async function PostDonation(
  params: PostDonationParameters,
  forceError?: boolean
): Promise<PostDonationResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: randomId(), authToken: randomId() } });
}
export async function GetDonationList(
  params: GetDonationListParameters,
  forceError?: boolean
): Promise<GetDonationListResult> {
  if (forceError) return delay(mockError());
  const { limit = 10, lastId } = params;
  const { items, total } = getPagedList(donationList, limit, lastId);
  return delay({
    kind: 'ok',
    data: { items, total }
  });
}
export async function GetDonation(
  params: GetDonationParameters,
  forceError?: boolean
): Promise<GetDonationResult> {
  if (forceError) return delay(mockError());
  const item = donationList.find(x => x.id === params.id) || donationList[0];
  return delay({ kind: 'ok', data: item });
}
export async function PutDonation(
  params: PutDonationParameters,
  forceError?: boolean
): Promise<PutDonationResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: params.id } });
}

// ------ 用户相关 ------
const userList: any[] = [];
for (let i = 0; i < 10; i++) {
  const id = randomId();
  userList.push({
    id,
    chineseName: genText(4, true),
    communicationLanguage: pick(['en', 'zh-CN', 'zh-HK']),
    donatedAmount: Math.floor(Math.random() * 10000),
    email: `user${i}@test.com`,
    englishGivenName: genText(4),
    englishSurname: genText(6),
    mobileNumber: '13' + Math.floor(Math.random() * 100000000).toString().padStart(8, '0'),
    receivedDonationAmount: Math.floor(Math.random() * 20000),
    salutation: pick(['Mr.', 'Ms.', 'Dr.']),
    receivedDonationAt: moment().add(i + 1, 'days').toISOString(),
    donatedAt: moment().add(i + 2, 'days').toISOString(),
  });
}
export async function GetUser(
  params: GetUserParameters,
  forceError?: boolean
): Promise<GetUserResult> {
  if (forceError) return delay(mockError());
  const item = userList.find(x => x.id === params.id) || userList[0];
  return delay({ kind: 'ok', data: item });
}
export async function PutUser(
  params: PutUserParameters,
  forceError?: boolean
): Promise<PutUserResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: params.id } });
}


export async function PostPaymePayment(
  params: PostPaymePaymentParameters,
  forceError?: boolean
): Promise<PostPaymePaymentResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: randomId(), appLink: `${config.REACT_APP_HOST}/donation/0/amount/receipt/xsss`, webLink: `${config.REACT_APP_HOST}/donation/0/amount/receipt/xsss` } });
}

export async function PostContact(
  params: PostContactParameters,
  forceError?: boolean
): Promise<PostContactResult> {
  if (forceError) return delay(mockError());
  return delay({ kind: 'ok', data: { id: randomId() } });
}


export const MockApi = {
  PostAssetUpload,
  GetAsset,
  PostAuthRegister,
  PostAuthLogin,
  PostAuthSendVerificationCode,
  PostAuthCheckAccountExists,
  PostAuthLogout,
  PostCampaign,
  GetCampaignList,
  GetCampaign,
  PutCampaign,
  DeleteCampaign,
  GetCampaignTheme,
  GetCampaignThemeList,
  PostDonation,
  GetDonationList,
  GetDonation,
  PutDonation,
  GetUser,
  PutUser,
  PostAuthConfirmEmail,
  PostPaymePayment,
  PostContact,
};

export const isMockApi = true;
